import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AuthApiService } from '../../core/services/auth-api.service';
import { AuthActions } from './auth.actions';
import { SocketActions } from '../socket/socket.actions';
import { Store } from '@ngrx/store';
import { ConfigService } from '../../core/config/config.service';
import { getAccessToken, getRefreshToken } from './auth.selectors';
import {
  getFirstAllowedRoute,
  isRouteAllowed,
} from '../../shared/utils/navigation-permissions.util';
import { ToastService } from '../../shared/services/toast.service';
import { I18nService } from '../../core/i18n/i18n.service';
import { AppActions } from '../app/app.actions';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { PushNotificationService } from '../../core/notifications/push-notification.service';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authApiService = inject(AuthApiService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(I18nService);
  private readonly errorHandlerSvc = inject(ErrorHandlerService);
  private readonly pushNotificationSvc = inject(PushNotificationService);

  private readonly TEMPORARY_PASSWORD_ERROR_CODE = 'TemporaryPassword';

  public readonly login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ request }) =>
        this.authApiService.login(request).pipe(
          map((response) => AuthActions.loginSuccess({ response })),
          catchError((error: Error) => {
            if (
              this.errorHandlerSvc.getKey(error) ===
              this.TEMPORARY_PASSWORD_ERROR_CODE
            ) {
              const url = this.errorHandlerSvc.getValue(error);
              this.router.navigateByUrl(`/${url}`);
              return of(AuthActions.markLoaded());
            }

            return of(
              AuthActions.loginFailure({
                error: this.errorHandlerSvc.getMessage(error) || 'Login failed',
              }),
            );
          }),
        ),
      ),
    ),
  );

  public readonly authSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess, AuthActions.tokenLoginSuccess),
        tap(({ response }) => {
          this.i18n.setLanguage(response.company?.language);

          if (this.configService.websocketEnabled()) {
            this.store.dispatch(SocketActions.connect());
          }

          if (this.configService.pushNotificationsEnabled()) {
            this.pushNotificationSvc.requestAndSubscribe();
          }

          const roles = response.roles ?? [];
          const fallbackRoute = getFirstAllowedRoute(roles);
          this.router.navigateByUrl(fallbackRoute);
        }),
      ),
    { dispatch: false },
  );

  public readonly tokenLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.tokenLogin),
      switchMap(({ token }) =>
        this.authApiService.tokenAuthentication(token).pipe(
          map((response) => AuthActions.tokenLoginSuccess({ response })),
          catchError((error: Error) =>
            of(
              AuthActions.tokenLoginFailure({
                error: error.message || 'Login failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly changeHotel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changeHotel),
      withLatestFrom(
        this.store.select(getAccessToken),
        this.store.select(getRefreshToken),
      ),
      switchMap(([{ hotelId }, accessToken, refreshToken]) =>
        this.authApiService
          .changeHotel(hotelId, accessToken!, refreshToken!)
          .pipe(
            map((response) => AuthActions.changeHotelSuccess({ response })),
            catchError((error: Error) =>
              of(
                AuthActions.changeHotelFailure({
                  error: error.message || 'Login failed',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public readonly changeHotelSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changeHotelSuccess),
        tap(({ response }) => {
          this.i18n.setLanguage(response.company?.language);

          this.store.dispatch(SocketActions.disconnect());

          if (this.configService.websocketEnabled()) {
            this.store.dispatch(SocketActions.connect());
          }

          if (this.configService.pushNotificationsEnabled()) {
            this.pushNotificationSvc.requestAndSubscribe();
          }

          this.store.dispatch(AppActions.contextChanged());

          const currentUrl = this.router.url;
          const roles = response.roles ?? [];

          if (isRouteAllowed(currentUrl, roles)) {
            this.router.navigateByUrl(currentUrl, {
              onSameUrlNavigation: 'reload',
            });

            return;
          }

          const fallbackRoute = getFirstAllowedRoute(roles);
          this.router.navigateByUrl(fallbackRoute);
        }),
      ),
    { dispatch: false },
  );

  public readonly changeHotelFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changeHotelFailure),
        tap(({ error }) => {
          this.store.dispatch(SocketActions.disconnect());

          if (this.configService.websocketEnabled()) {
            this.store.dispatch(SocketActions.connect());
          }

          this.toast.error(this.i18n.translate(error));

          this.router.navigateByUrl('/login');
        }),
      ),
    { dispatch: false },
  );

  public readonly logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        switchMap(() =>
          this.authApiService.logout().pipe(
            catchError(() => of(null)),
            tap(() => {
              this.store.dispatch(SocketActions.disconnect());
              this.router.navigateByUrl('/login');
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  public readonly resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      switchMap(({ request }) =>
        this.authApiService.resetPassword(request.username).pipe(
          map(() => {
            this.toast.success(
              this.i18n.translate('auth.passwordResetInstructions'),
            );
            this.router.navigateByUrl('/login');
            return AuthActions.resetPasswordSuccess();
          }),
          catchError((error: Error) =>
            of(
              AuthActions.resetPasswordFailure({
                error: error.message || 'Password reset failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly setNewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setNewPassword),
      switchMap(({ request }) =>
        this.authApiService
          .setNewPassword(
            request.userId,
            request.hash,
            request.password,
            request.confirmPassword,
          )
          .pipe(
            map(() => {
              this.toast.success(
                this.i18n.translate('auth.passwordResetSuccess'),
              );
              this.router.navigateByUrl('/login');
              return AuthActions.setNewPasswordSuccess();
            }),
            catchError((error: Error) =>
              of(
                AuthActions.setNewPasswordFailure({
                  error: error.message || 'Password reset failed',
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
